<template>
  <div class="codes">
    <div class="d-flex align-center pt-5">
      <router-link to="/products">
        <v-btn class="ml-3" icon><v-icon color="black">mdi-arrow-left</v-icon></v-btn>
      </router-link>
      <div class="page-title mono pl-3">{{$t('codes_all_qrs')}}</div>
      <codes-csv ref="csv-add-codes" v-on:codes-activated="resetBulkSelection" class="ml-4"></codes-csv>
    </div>
    <div class="d-flex align-center pt-5 mx-8">
      <codes-search class="flex-grow-1 mr-4" ref="codeSearch" :campaign="selectedCampaign.id"></codes-search>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="download-btn" color="primary" raised :loading="downloadingCsv" :disabled="!csvCount" v-bind="attrs" v-on="on">
            {{$t('download')}}<v-icon class="download-btn-arrow">mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="$refs.qrDownload.open()">
            <v-list-item-title>{{$t('download_qr_code_images')}}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="csvCount <= 1000" @click="downloadCodesCsv">
            <v-list-item-title>{{$t('download_urls_in_csv')}}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <download ref="qrDownload" :hide-activator="true" mode="codes" :count="selectAllEnabled ? filteredCodeCount : selectedCodesIndexes.length" :select-all="selectAllEnabled" :codes="selectedCodes" :campaign="selectedCampaign.id" :product="selectedCodes.length === 1 ? selectedCodes[0].product.id : undefined" :enable-qr-text-settings="selectedCodes.length === 1 ? true : false"></download>
    </div>

    <div v-show="filteredProductCount" class="d-flex align-center justify-space-between search-settings mx-8">
      <div class="d-flex align-center flex-wrap">
        <v-checkbox hide-details :value="selectAllEnabled" @click="toggleSelectAll()"></v-checkbox>
        <div class="search-label mr-4">{{$t('codes_selected')}}: {{ selectAllEnabled ? filteredCodeCount : selectedCodesIndexes.length }}</div>
        <div v-if="activeCodesFilters.length" class="search-label mr-4">{{$t('codes_results')}}: {{filteredCodeCount}}</div>
        <div class="search-label mr-2">{{$t('filters')}}:</div>
        <div class="search-label" v-if="!activeCodesFilters.length">{{$t('none')}}</div>
        <v-chip class="ml-2" close color="#f1f1f1" text-color="#0789b2" @click="editActiveFilter(filter)" @click:close="removeFilter(filter)" :key="`active-filters-${index}`" v-for="(filter, index) in activeCodesFilters">
          {{filter.name}}: {{filter.value}}
        </v-chip>
      </div>
      <v-spacer></v-spacer>
      <div v-show="codesLoaded" class="search-label mr-7 flex-shrink-0 mono">{{$t('total')}}: {{filteredCodeCount}}</div>
      <button @click="showUrl = !showUrl" class="d-flex show-url-btn">
        <v-img :src="visibilityImgUrl" />
        <span v-if="showUrl">{{$t('hide_url_prefixes')}}</span>
        <span v-if="!showUrl">{{$t('show_url_prefixes')}}</span>
      </button>
    </div>

    <div class="code-list-ctn mx-8">
      <v-progress-linear v-show="!codesLoaded" indeterminate></v-progress-linear>
      <div v-show="codesLoaded" >
        <div class="code-line py-1 d-flex align-center" :class="{'selected': selectedCodesIndexes.indexOf(codeIndex) >= 0 }" v-for="(code, codeIndex) in codes" :key="code.id">
          <div class="d-flex flex-wrap align-center">
            <div class="d-flex flex-no-wrap">
              <v-checkbox hide-details color="#15bbf0" :value="selectedCodesIndexes.indexOf(codeIndex) >= 0" @click="toggleSelectCode(codeIndex)"></v-checkbox>
              <div class="mono word-break-all">
                <span>{{ showUrl ? (code.scm_data.scantrust_url_prefix ? code.scm_data.scantrust_url_prefix : singleNonSystemPrefix ? singleNonSystemPrefix : systemPrefix) : ''}}{{code.message}} |</span>
                <span @click="addProductFilter(code.product)" class="url product-name pl-2">{{code.product.name}}</span>
              </div>
            </div>

            <v-chip class="ml-2" v-show="value" @click="addFilter(name, value)" color="#f1f1f1" text-color="#0789b2" :key="`code-${code.id}-tags-${name}-${value}`" v-for="(value, name) in filterScmData(code.scm_data)">
              {{ name === 'scantrust_upload_batch' ? $t('upload_batch') : name.startsWith('gs1_') ? name.split('gs1_')[1] : name }}: {{value}}
            </v-chip>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex flex-shrink-0 align-center">
            <div class="date mono mr-2">{{code.activated_at ? formatDate(code.activated_at) : formatDate(code.creation_date) }}</div>
            <code-tag-editor :tags="scmFields" :codes="[code]" :campaign="selectedCampaign.id"></code-tag-editor>
            <code-change-product v-if="!isGS1Code(code.message)" v-on:code-transfered="loadCodes" :codes="[code]"></code-change-product>
            <v-menu offset-y v-if="isCodeSpacesEnabled">
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="manageCodes(code.product)">
                  <v-list-item-title>{{$t('qrcode_view_product')}}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!isGS1Code(code.message)" @click="handleDisplayUniqueIdEdit(code)">
                  <v-list-item-title>{{$t('qrcode_edit_unique_identifier')}}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleDisplayGS1Edit(code)">
                  <v-list-item-title>{{isGS1Code(code.message) ? $t('qrcode_edit_gs1_link') : $t('qrcode_convert_to_gs1_link')}}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>
      <div v-if="codesLoaded && !codes.length" class="pa-2">
        {{$t('codes_not_found')}}
      </div>
      <div class="pagination-ctn mt-5 mb-4">
        <v-pagination v-show="codesLoaded && codes.length" class="flex-grow-0" color="primary" :total-visible="7" v-model="page" :length="nbPages">
        </v-pagination>
      </div>
    </div>

    <v-overlay :value="isLoadingProduct">
      <v-progress-circular indeterminate size="48"></v-progress-circular>
    </v-overlay>

    <v-dialog width="600" persistent v-model="prefixDialog">
      <v-card>
        <v-card-title>{{$t('select_url_prefix')}}</v-card-title>
        <v-card-text class="my-2 pb-0">
          <v-select v-model="selectedPrefix" :items="formattedPrefixes" :label="$t('all_url_prefixes')" item-text="displayText" item-value="prefix"
            @change="showPrefixRequiredMessage = false"
            persistent-hint :hint="$t('prefix_will_be_associated_with_codes_selected')">
            <template v-slot:selection="{ item }">
              <div class="prefix">{{item.displayText}}</div>
            </template>
          </v-select>
          <span class="color-error mt-2 d-block" v-if="showPrefixRequiredMessage">{{$t('prefix_required')}}</span>
        </v-card-text>
        <v-card-actions>
          <div class="mr-auto"></div>
          <v-btn class="mr-2" @click="closePrefixDialog" :disabled="downloadingCsv" text>{{$t('cancel')}}</v-btn>
          <v-btn class="mr-2" color="primary" :loading="downloadingCsv" @click="confirmPrefixAndDownloadCsv" text>{{$t('download_codes_csv')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <generate-qr-code-dialog v-if="displayDialog && selectedCodeProduct" :display-dialog.sync="displayDialog" :selected-product="selectedCodeProduct" :is-converting-to-gs1="isConvertingToGs1"/>

    <edit-confirmation-modal :display-edit-modal.sync="displayEditModal" @open-dialog="handleDisplayQrCodeDialog" :selected-code="code"/>
  </div>
</template>

<script>

import axios from 'axios'
import CodesSearch from '@/components/CodesSearch'
import CodesCSV from '@/components/CodesCSV'
import Download from '@/components/Download'
import { mapGetters, mapMutations } from 'vuex'
import CodeTagEditor from '@/components/CodeTagEditor'
import CodeChangeProduct from '@/components/CodeChangeProduct'
import GenerateQrCodeDialog from '@/components/GenerateQrCodeDialog'
import EditConfirmationModal from '@/components/EditConfirmationModal'
import Utils from '@/utils/utils'
import * as Papa from 'papaparse'

export default {
  name: 'codes',
  computed: {
    ...mapGetters(['scmFieldLoaded', 'scmFields', 'prefixes', 'prefixesLoaded', 'systemPrefix', 'selectedCampaign', 'activeCodesFilters', 'codes', 'filteredCodeCount', 'codesLoaded', 'filteredProductCount', 'profile']),

    isCodeSpacesEnabled () {
      return this.profile?.company?.code_spaces_enabled
    },

    nbPages () {
      return Math.ceil(this.filteredCodeCount / this.pageSize)
    },

    visibilityImgUrl () {
      return this.showUrl ? require('../assets/visibility-off.svg') : require('../assets/visibility-on.svg')
    },

    nonSystemPrefixes () {
      return this.prefixes.filter((prefix) => prefix.type !== 'system')
    },

    singleNonSystemPrefix () {
      return this.nonSystemPrefixes.length === 1 ? this.nonSystemPrefixes[0].prefix : null
    },

    formattedPrefixes () {
      return this.nonSystemPrefixes.map((item) => {
        let displayText = item.prefix
        if (item.type === 'hosted-domain') {
          displayText += ` (${this.$t('prefix_custom_domain')})`
        } else if (item.type === 'registered') {
          displayText += ` (${this.$t('prefix_self_hosted')})`
        }
        return { ...item, displayText }
      })
    },

    csvCount () {
      return this.selectAllEnabled ? this.filteredCodeCount : this.selectedCodesIndexes.length
    }
  },
  components: {
    'codes-csv': CodesCSV,
    CodesSearch,
    CodeTagEditor,
    CodeChangeProduct,
    Download,
    GenerateQrCodeDialog,
    EditConfirmationModal
  },
  watch: {
    '$route.params' (newVal) {
      if (newVal && this.$route.name === 'codes') {
        if (!this.scmFieldLoaded) {
          this.$store.dispatch('loadScmFields', this.selectedCampaign.id)
        }
        if (newVal.openBulkCreation) {
          this.$refs['csv-add-codes'].open()
        }
        this.loadCodes()
      }
    },

    page (newVal) {
      if (newVal) {
        this.selectAllEnabled = false
        this.selectedCodesIndexes = []
        this.loadCodes(newVal - 1)
      }
    },
    selectedCodesIndexes (newVal) {
      this.selectedCodes = []
      newVal.forEach((index) => {
        this.selectedCodes.push(this.codes[index])
      })
    }
  },
  created () {
    if (!this.scmFieldLoaded) {
      this.$store.dispatch('loadScmFields', this.selectedCampaign.id)
    }
    if (!this.prefixesLoaded) {
      this.$store.dispatch('loadPrefixes')
    }
  },
  mounted () {
    if (this.$route.params && this.$route.params.openBulkCreation) {
      this.$refs['csv-add-codes'].open()
    }
  },
  data () {
    return {
      selectAllEnabled: false,
      page: 1,
      pageSize: 20,
      selectedCodes: [],
      selectedCodesIndexes: [],
      cancelToken: null,
      showUrl: true,
      // Edit unique identifier variables
      displayEditModal: false,
      displayDialog: false,
      code: null,
      selectedCodeProduct: null,
      isConvertingToGs1: false,
      isLoadingProduct: false,
      downloadingCsv: false,
      prefixDialog: false,
      selectedPrefix: '',
      showPrefixRequiredMessage: false,
      pendingCsvCodes: [],
      prefixBatchSize: 100
    }
  },
  methods: {
    ...mapMutations({
      removeActiveFilter: 'REMOVE_CODE_ACTIVE_FILTER'
    }),

    editActiveFilter (filter) {
      this.$refs.codeSearch.selectTag(filter, filter.searchValue || '')
    },

    formatDate (string) {
      return Utils.formatDate(string, true)
    },

    codeFullUrl (code, fallbackPrefix) {
      const customPrefixes = this.nonSystemPrefixes
      let prefix
      if (customPrefixes.length === 1) {
        prefix = customPrefixes[0].prefix
      } else if (customPrefixes.length > 1 && code.scm_data.scantrust_url_prefix) {
        prefix = code.scm_data.scantrust_url_prefix
      } else if (customPrefixes.length > 1 && fallbackPrefix) {
        prefix = fallbackPrefix
      } else {
        prefix = this.systemPrefix
      }
      const gs1 = code.scm_data.gs1_data_attributes ? `?${code.scm_data.gs1_data_attributes}` : ''
      return `${prefix}${code.message}${gs1}`
    },

    async downloadCodesCsv () {
      if (!this.nonSystemPrefixes.length && !this.systemPrefix) {
        this.$store.dispatch('showSnackBar', { message: this.$t('prefix_missing') })
        return
      }

      this.downloadingCsv = true
      try {
        const codes = this.selectAllEnabled
          ? await this.$store.dispatch('loadAllCodes', { count: this.filteredCodeCount, campaign: this.selectedCampaign.id })
          : this.selectedCodes

        // codes without a stored prefix are ambiguous when the company has several prefixes,
        // so let the user pick the one to use (and store it on those codes)
        if (this.nonSystemPrefixes.length > 1 && codes.some((code) => !code.scm_data.scantrust_url_prefix)) {
          this.pendingCsvCodes = codes
          this.prefixDialog = true
          return
        }

        this.generateCodesCsv(codes)
      } catch (err) {
        this.$store.dispatch('showSnackBar', { message: err?.message || this.$t('download_error_msg') })
      } finally {
        this.downloadingCsv = false
      }
    },

    async confirmPrefixAndDownloadCsv () {
      if (!this.selectedPrefix) {
        this.showPrefixRequiredMessage = true
        return
      }

      this.downloadingCsv = true
      try {
        const codes = this.pendingCsvCodes
        const codesWithoutPrefix = codes.filter((code) => !code.scm_data.scantrust_url_prefix)
        await this.updatePrefixForCodes(codesWithoutPrefix)
        this.generateCodesCsv(codes, this.selectedPrefix)
        this.closePrefixDialog()
      } catch (err) {
        this.$store.dispatch('showSnackBar', { message: err?.message || this.$t('download_error_msg') })
      } finally {
        this.downloadingCsv = false
      }
    },

    async updatePrefixForCodes (codes) {
      for (let index = 0; index < codes.length; index += this.prefixBatchSize) {
        const batch = codes.slice(index, index + this.prefixBatchSize)
        let attempts = 0
        while (true) {
          try {
            await this.$store.dispatch('updateSelectedPrefixes', {
              codes: batch,
              prefix: this.selectedPrefix,
              reference: 'QR manager: updating scantrust prefix'
            })
            break
          } catch (err) {
            attempts += 1
            if (err?.response?.status !== 429 || attempts > 5) throw err
            await new Promise((resolve) => setTimeout(resolve, 10000))
          }
        }
      }
    },

    closePrefixDialog () {
      this.prefixDialog = false
      this.selectedPrefix = ''
      this.showPrefixRequiredMessage = false
      this.pendingCsvCodes = []
    },

    generateCodesCsv (codes, fallbackPrefix) {
      const rows = codes.map((code) => ({
        product_name: code.product ? code.product.name : '',
        sku: code.product ? code.product.sku : '',
        url: this.codeFullUrl(code, fallbackPrefix),
        extended_id: code.message
      }))

      const blob = new Blob(['\ufeff' + Papa.unparse(rows)], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'qr-codes.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    resetBulkSelection () {
      this.selectAllEnabled = false
      this.selectedCodesIndexes = []
    },

    toggleSelectAll () {
      this.selectAllEnabled = !this.selectAllEnabled
      this.selectedCodesIndexes = []
      if (this.selectAllEnabled) {
        this.codes.forEach((code, index) => {
          this.selectedCodesIndexes.push(index)
        })
      }
    },

    toggleSelectCode (index) {
      if (this.selectedCodesIndexes.indexOf(index) >= 0) {
        if (this.selectAllEnabled) {
          this.selectAllEnabled = false
        }
        this.selectedCodesIndexes.splice(this.selectedCodesIndexes.indexOf(index), 1)
      } else {
        this.selectedCodesIndexes.push(index)
      }
    },

    removeFilter (filter) {
      this.removeActiveFilter(filter)
      this.cancellableLoad({ campaign: this.selectedCampaign.id })
      this.resetBulkSelection()
    },

    cancellableLoad (params) {
      if (this.cancelToken && this.cancelToken.cancel) {
        this.cancelToken.cancel('Cancelling request')
      }

      this.cancelToken = axios.CancelToken.source()
      this.$store.dispatch('loadCodes', { ...params, cancelToken: this.cancelToken })
    },

    loadCodes (page) {
      this.cancellableLoad({ campaign: this.selectedCampaign.id, offset: page ? page * this.pageSize : 0 })
    },

    manageCodes (product) {
      this.$router.push({ name: 'products.codes', params: { id: product.id, product: product } })
    },

    addProductFilter (product) {
      const filter = {
        name: this.$t('product'),
        key: 'products',
        value: product.name,
        searchKey: 'products',
        searchValue: product.id
      }

      this.$store.dispatch('addCodeFilter', filter)
      this.cancellableLoad({ campaign: this.selectedCampaign.id })
    },

    addFilter (key, value) {
      const filter = {
        name: key.startsWith('gs1_') ? key.split('gs1_')[1] : key,
        key: key,
        value: value,
        searchKey: `scm_${key}`,
        searchValue: value
      }

      this.$store.dispatch('addCodeFilter', filter)
      this.cancellableLoad({ campaign: this.selectedCampaign.id })
    },

    filterScmData (scmData) {
      if (scmData) {
        const filteredData = {}
        Object.keys(scmData).forEach(key => {
          if (!key.startsWith('scantrust_') && key !== 'gs1_link') {
            filteredData[key] = scmData[key]
          }
        })
        return filteredData
      }
    },
    isGS1Code (extendedId) {
      return Utils.isGS1Identifier(extendedId)
    },
    handleDisplayUniqueIdEdit (code) {
      this.code = code
      this.isConvertingToGs1 = false
      this.displayEditModal = true
    },
    handleDisplayGS1Edit (code) {
      this.code = code
      this.isConvertingToGs1 = !this.isGS1Code(code.message)
      this.displayEditModal = true
    },
    async handleDisplayQrCodeDialog () {
      this.isLoadingProduct = true
      try {
        const product = await this.$store.dispatch('loadProduct', this.code.product.id)
        if (!product) return
        await this.$store.dispatch('loadProductCodeCount', { product: this.code.product.id })
        this.selectedCodeProduct = product
        this.displayDialog = true
      } catch (err) {
        console.log(err)
      } finally {
        this.isLoadingProduct = false
      }
    }
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.codes
  overflow: auto
  height: calc(100vh - 63px)
  @include xl
    margin-left: 14%
    padding-right: 13%

.code-list-ctn
  border-top: 1px solid rgba(0, 0, 0, 0.3)
  .code-line
    border-bottom: 1px solid rgba(33, 33, 33, 0.08)

.search-settings
  padding: 15px 0px
  .search-label, .show-url-btn span
    font-size: 16px
    color: #222222
    opacity: 0.5
    text-transform: uppercase
    letter-spacing: 0.8px
  .show-url-btn
    column-gap: 8px
    cursor: pointer
    span
      text-wrap: nowrap
      &:hover
        color: $primary

.search-settings, .code-line
  .v-input--checkbox
    padding-top: 0 !important
    margin-top: 0 !important

.code-checkbox
  padding: 10px
  border-bottom: 1px solid rgba(33,33,33,0.08)

.url
  color: #333
  font-style: italic
  font-size: 14px

.product-name
  cursor: pointer

.pagination-ctn
  text-align: right

.date
  color: #999999

.word-break-all
  word-break: break-all

.download-btn
  padding-right: 0 !important
  ::v-deep .v-btn__content
    align-self: stretch
  .download-btn-arrow
    align-self: stretch
    margin-left: 12px
    padding: 0 4px
    background-color: rgba(0, 0, 0, 0.08)

.prefix
  max-width: 100%

.color-error
  color: red

</style>
